import Button from './Button';

const PAGE_SIZES = [20, 50, 100];

export default function Pagination({ total, limit, offset, onChange, onLimitChange }) {
    const totalPages = Math.ceil(total / limit);
    const page = Math.floor(offset / limit) + 1;

    if (total <= 0) return null;

    const start = offset + 1;
    const end = Math.min(offset + limit, total);

    const getVisiblePages = () => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

        const pages = [];
        pages.push(1);
        if (page > 3) pages.push(null);
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
        if (page < totalPages - 2) pages.push(null);
        if (totalPages > 1) pages.push(totalPages);
        return pages;
    };

    return (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3 mt-3 border-t border-gray-100">
            <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 whitespace-nowrap">
                    {start}-{end} of {total.toLocaleString()}
                </span>
                {onLimitChange && (
                    <select
                        value={limit}
                        onChange={(e) => onLimitChange(Number(e.target.value))}
                        className="h-7 pl-2 pr-6 text-[11px] text-gray-500 border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500/20 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2210%22%20height%3D%2210%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:10px] bg-[right_4px_center] bg-no-repeat"
                    >
                        {PAGE_SIZES.map(s => (
                            <option key={s} value={s}>{s} / page</option>
                        ))}
                    </select>
                )}
            </div>
            {totalPages > 1 && (
                <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="xs" disabled={page <= 1} onClick={() => onChange(0)} className="!px-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                            <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
                        </svg>
                    </Button>
                    <Button variant="ghost" size="xs" disabled={page <= 1} onClick={() => onChange((page - 2) * limit)} className="!px-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                            <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                        </svg>
                    </Button>
                    {getVisiblePages().map((p, i) =>
                        p === null ? (
                            <span key={`e${i}`} className="px-1 text-xs text-gray-300">...</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => onChange((p - 1) * limit)}
                                className={`min-w-[28px] h-7 px-1.5 text-xs rounded transition-colors ${
                                    p === page
                                        ? 'bg-gray-900 text-white font-medium'
                                        : 'text-gray-500 hover:bg-gray-100'
                                }`}
                            >
                                {p}
                            </button>
                        )
                    )}
                    <Button variant="ghost" size="xs" disabled={page >= totalPages} onClick={() => onChange(page * limit)} className="!px-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                    </Button>
                    <Button variant="ghost" size="xs" disabled={page >= totalPages} onClick={() => onChange((totalPages - 1) * limit)} className="!px-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                            <path fillRule="evenodd" d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02zm-6 0a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                    </Button>
                </div>
            )}
        </div>
    );
}
